import { ref, get, push, update } from 'firebase/database';
import { database } from '../firebaseConfig';
import { contactFieldsFromInterest, contactPatch, findContactId } from './pipelineContact';
import { referralNotifyTask } from './referralTasks';
import { getAccessible, newOwnership, indexOps, ownerOf, canManageRecord, KIND_PATH } from './recordAccess';

const nowIso = () => new Date().toISOString();

// Creates or updates the Leads contact for a pipeline record with ALL its data (phone, email, address,
// consents, notes, source, contact type, every additional contact). Each additional contact that has a
// phone or email also gets its own contact, linked both ways; ones with only a name are listed on the
// main contact. Links: <path>/<id>/contactId and crm/contacts/<cid>/<kind>InterestIds/<id> = true.
export const syncPipelineContactApp = async (kind, path, interest, uid) => {
  const linkKey = kind === 'buyer' ? 'buyerInterestIds' : 'sellerInterestIds';
  const label = kind === 'buyer' ? 'Buyer interest' : 'Seller interest';
  const isAdmin = ((await get(ref(database, `users/${uid}/role`)).catch(() => null))?.val() || '') === 'Admin';
  // Only contacts this user can see (their own + shared; admins all). Someone else's private contact is
  // never matched, changed or revealed; the user gets their own contact instead.
  const contacts = Object.fromEntries((await getAccessible('contacts', uid, isAdmin)).map(({ id, ...c }) => [id, c]));
  if (interest.contactId && !contacts[interest.contactId]) return interest.contactId; // linked to a contact you can't see: leave it
  const fields = contactFieldsFromInterest(kind, interest);
  let cid = (interest.contactId && contacts[interest.contactId]) ? interest.contactId : findContactId(contacts, fields);
  const assigned = interest.assignedAgentId || uid || '';
  const pipeKind = kind === 'buyer' ? 'buyerInterests' : 'sellerInterests';
  const pipeOwner = ownerOf(interest) || uid;
  const newContact = (data) => {
    const r = push(ref(database, 'crm/contacts'));
    const rec = { ...data, ...newOwnership(pipeOwner, interest), ownerType: 'agent', ownerId: pipeOwner, assignedToId: pipeOwner, createdAt: nowIso(), updatedAt: nowIso(), createdBy: uid || null };
    return update(ref(database), { [`crm/contacts/${r.key}`]: rec, ...indexOps('contacts', r.key, rec) }).then(() => r.key);
  };
  if (cid) {
    const patch = contactPatch(contacts[cid], fields, false);
    patch[`${linkKey}/${interest.id}`] = true; patch.updatedAt = nowIso();
    await update(ref(database, `crm/contacts/${cid}`), patch);
    // Sharing carries over: whoever can see the contact can see its pipeline record too.
    const c = contacts[cid];
    const want = { ...(interest.sharedWith || {}), ...(c.sharedWith || {}) };
    if (ownerOf(c)) want[ownerOf(c)] = true;
    delete want[pipeOwner];
    const added = Object.keys(want).filter((u) => !(interest.sharedWith || {})[u]);
    if (added.length && canManageRecord(interest, uid, isAdmin)) {
      await update(ref(database), { [`${KIND_PATH[pipeKind]}/${interest.id}/sharedWith`]: want, ...indexOps(pipeKind, interest.id, { ownerUid: pipeOwner, sharedWith: want }, interest) }).catch((e) => console.warn('Could not carry sharing to the pipeline record', e));
    }
  } else {
    cid = await newContact({ ...fields, source: fields.source || label, createdFrom: label, [linkKey]: { [interest.id]: true }, tags: [label] });
  }
  for (const c of fields.additionalContacts) {
    if (!c.phone && !c.email) continue;
    let aid = findContactId(contacts, c);
    if (aid === cid) continue;
    if (aid) {
      const ex = contacts[aid] || {};
      const p = { [`linkedContactIds/${cid}`]: c.relationship || 'Related', updatedAt: nowIso(),
        contactTypes: [...new Set([...(ex.contactTypes || []), ...fields.contactTypes])] };
      if (!ex.phone && c.phone) p.phone = c.phone; if (!ex.email && c.email) p.email = c.email;
      await update(ref(database, `crm/contacts/${aid}`), p);
    } else {
      aid = await newContact({ firstName: c.firstName, lastName: c.lastName, name: [c.firstName, c.lastName].filter(Boolean).join(' '), phone: c.phone, email: c.email,
        contactTypes: fields.contactTypes, source: fields.source || label, tags: [label], relationship: c.relationship, linkedContactIds: { [cid]: c.relationship || 'Related' },
        consentText: false, consentEmail: false, [linkKey]: { [interest.id]: true } });
      contacts[aid] = { phone: c.phone, email: c.email, ownerUid: pipeOwner };
    }
    await update(ref(database, `crm/contacts/${cid}`), { [`linkedContactIds/${aid}`]: c.relationship || 'Related' });
  }
  // Referred contact entering the pipeline: a task for the agent to thank/update the referrer (nothing is sent).
  try { const c = (await get(ref(database, `crm/contacts/${cid}`))).val(); await referralNotifyTask(kind, interest, cid, c, assigned); } catch (e) { console.error('Referral task skipped', e); }
  if (cid !== interest.contactId) await update(ref(database, `${path}/${interest.id}`), { contactId: cid });
  return cid;
};
